import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, InputAdornment, SxProps, TextField } from '@mui/material';
import debounce from 'lodash/debounce';
import * as Icons from '../icons';

export type SearchInputProps = {
  fullWidth?: boolean;
  autoFocus?: boolean;
  /**
   * Controlled value. Omit to use uncontrolled mode.
   */
  value?: string;
  /**
   * Initial value for uncontrolled mode.
   */
  defaultValue?: string;
  placeholder?: string;
  label?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  sx?: SxProps;
  startAdornmentSx?: SxProps;
  hasStartAdornment?: boolean;
  InputProps?: object;
  debounceMs?: number;
  /**
   * Optional debounced commit hook. Called after `debounceMs` of inactivity.
   * Use this for expensive operations (filtering, network requests).
   */
  onDebouncedChange?: (value: string) => void;
};

export function SearchInput({
  value,
  defaultValue,
  placeholder,
  label,
  onChange,
  onClear,
  fullWidth = false,
  autoFocus = false,
  hasStartAdornment = true,
  startAdornmentSx = {},
  debounceMs = 500,
  onDebouncedChange,
  ...props
}: SearchInputProps) {
  // Support controlled and uncontrolled modes:
  // if parent supplies `value`, that drives the input; otherwise use internal state.
  const [internalValue, setInternalValue] = useState<string>(value ?? defaultValue ?? '');
  const { t } = useTranslation();

  // Keep internal state in sync when the parent provides a changing `value`
  useEffect(() => {
    if (typeof value !== 'undefined' && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]);

  // Keep the latest callbacks in refs so the debounced commit can read them
  // without being rebuilt. Rebuilding it on every callback-identity change
  // (e.g. a parent re-render that recreates an inline handler) would cancel
  // any in-flight debounced call and silently drop the pending search.
  const onDebouncedChangeRef = useRef(onDebouncedChange);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onDebouncedChangeRef.current = onDebouncedChange;
    onChangeRef.current = onChange;
  });

  // Debounced commit: ONLY calls onDebouncedChange (falls back to onChange when
  // no debounced handler is provided). Stable across renders so a pending call
  // survives parent re-renders and only depends on `debounceMs`.
  const debouncedCommit = useMemo(() => {
    const runDebounced = debounce((v: string) => {
      onDebouncedChangeRef.current?.(v);
    }, debounceMs);

    return {
      call: (v: string) => {
        if (onDebouncedChangeRef.current && debounceMs) {
          runDebounced(v);
        } else {
          onChangeRef.current?.(v);
        }
      },
      cancel: () => runDebounced.cancel(),
    };
  }, [debounceMs]);

  // Cleanup on unmount
  useEffect(() => debouncedCommit.cancel, [debouncedCommit]);

  const clearSearch = () => {
    debouncedCommit.cancel();
    setInternalValue('');
    onChange && onChange('');
    onDebouncedChange && onDebouncedChange('');
    onClear && onClear();
  };

  const { InputProps, sx, ...rest } = props || {};
  const currentValue = typeof value !== 'undefined' ? value : internalValue;

  return (
    <TextField
      data-testid="search-input"
      variant="outlined"
      size="small"
      fullWidth={fullWidth}
      value={currentValue}
      label={label ?? t('searchInput:search')}
      InputLabelProps={{
        shrink: true,
      }}
      onChange={(event) => {
        const val = event.target.value;
        // update internal state for uncontrolled usage
        if (typeof value === 'undefined') {
          setInternalValue(val);
        }

        onChange?.(val);
        debouncedCommit.call(val);
      }}
      placeholder={placeholder}
      InputProps={{
        autoFocus,
        startAdornment: hasStartAdornment && (
          <InputAdornment position="start">
            <Icons.Search sx={{ color: 'grey.700', ...startAdornmentSx }} />
          </InputAdornment>
        ),
        endAdornment: currentValue ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={clearSearch}>
              <Icons.Close fontSize="small" data-testid="clear-search-input" />
            </IconButton>
          </InputAdornment>
        ) : null,
        ...InputProps,
        sx: { pl: hasStartAdornment ? 1.5 : 0, pr: 0.75 },
      }}
      sx={{
        minWidth: fullWidth ? 'unset' : '245px',
        ...sx,
      }}
      {...rest}
    />
  );
}
